import { useMemo, type FC } from 'react'
import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'
import { useFormikContext } from 'formik'

import { Card, StackColumn, useIsMobileVariant } from '@dhl-ui/components'
import { type FallbackType } from '@dhl/receiver-preference-api'

import { NotHomeFallbackSelectInput } from './NotHomeFallbackSelect.js'
import { AgreedPlaceForm, useCreateAgreedPlaceValidationSchema } from './AgreedPlaceForm.js'
import { type AgreedPlaceFormValues } from '../../model/model.js'

export type ToDoorPreferenceFormValues = AgreedPlaceFormValues & {
  fallback?: FallbackType
}

export const useCreateToDoorPreferenceValidationSchema = (): {
  validationSchema: Yup.Lazy<unknown>
} => {
  const { validationSchema: agreedPlaceValidationSchema } = useCreateAgreedPlaceValidationSchema()

  const validationSchema = useMemo(
    () =>
      Yup.lazy((values: ToDoorPreferenceFormValues) =>
        values.fallback === 'AgreedPlaceDelivery' ? agreedPlaceValidationSchema : Yup.object().shape({})
      ),
    [agreedPlaceValidationSchema]
  )

  return { validationSchema }
}

export const ToDoorPreferenceForm: FC = () => {
  const { t } = useTranslation()
  const { values } = useFormikContext<ToDoorPreferenceFormValues>()
  const isMobileVariant = useIsMobileVariant()

  const isAgreedPlaceOptionSelected = values.fallback === 'AgreedPlaceDelivery'

  return (
    <StackColumn gap={3}>
      <Card withPaddingTop>
        <Card.Header title={t('portal.profile.preferences.setupPage.toDoor.notHomeCardTitle')} />
        <Card.Content noPaddingTop={isMobileVariant}>
          <NotHomeFallbackSelectInput name="fallback" />
        </Card.Content>
      </Card>

      {!isAgreedPlaceOptionSelected && (
        <Card withPaddingTop>
          <Card.Header title={t('portal.profile.preferences.setupPage.toDoor.notPossibleCardTitle')} />
          <Card.Content noPaddingTop={isMobileVariant}>
            {t('portal.profile.preferences.setupPage.toDoor.notPossibleCardContent')}
          </Card.Content>
        </Card>
      )}

      {isAgreedPlaceOptionSelected && <AgreedPlaceForm />}
    </StackColumn>
  )
}
